import type { Article } from "../article-types";

export const moneyIndustriesArticles: Article[] = [
  {
    slug: "cash-flow-forecasting-guide",
    title: "Cash flow forecasting without the spreadsheet nightmare",
    metaTitle: "Cash Flow Forecasting for Small Business | NextEra",
    description:
      "Build a simple 13 week cash flow forecast for your Australian business, keep it updated in minutes each week, and spot cash gaps before they arrive.",
    category: "Cash flow",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "cash flow forecasting",
      "13 week cash flow forecast",
      "cash flow forecast small business",
      "how to forecast cash flow",
    ],
    intro:
      "Picture a business with $40,000 sitting in the bank and an owner who feels fine about it, right up until payroll, a supplier invoice and the quarterly BAS all land in the same week and empty the account almost overnight. That's the exact problem a 13 week cash flow forecast is built to catch, weeks before it becomes a scramble. You don't need software or a finance background to build one, just a short list of known payments, an honest read on when customers actually pay, and about ten minutes a week to keep it current. Here's how to put one together.",
    sections: [
      { type: "h2", text: "What a cash flow forecast actually does" },
      {
        type: "p",
        text: "Your profit and loss tells you what you earned. Your bank balance tells you what you've actually got. A cash flow forecast bridges the two, showing where your balance is likely to sit in four, eight or twelve weeks if things unfold the way you expect. That number decides whether you can hire someone, whether the BAS gets paid without drama, and whether a quiet month turns into a crisis or barely registers.",
      },
      {
        type: "p",
        text: "It isn't a prediction so much as an early warning system. See the balance dip below zero in week nine and you've got nine weeks to chase debtors, push back a purchase or ring the bank. Skip the forecast and you find out in week nine anyway, with none of those options left.",
      },
      { type: "h2", text: "Why 13 weeks is the sweet spot" },
      {
        type: "p",
        text: "Thirteen weeks covers a full quarter: one BAS cycle, one round of quarterly super, and enough of a business's seasonal rhythm to mean something. Go longer and the estimates turn into guesswork; annual forecasts have a place for planning but go stale within weeks. A rolling 13 week view, where you drop last week and tack a new one on the end, never goes out of date because it never stops being rebuilt.",
      },
      { type: "h2", text: "How to build one in about an hour" },
      {
        type: "p",
        text: "The structure is genuinely simple: one row for opening bank balance, a set of rows for cash in, a set for cash out, and a closing balance that rolls into the next week. That's it. Work through it in this order.",
      },
      {
        type: "ul",
        items: [
          "Start with today's actual bank balance, across every trading account you use.",
          "List cash in week by week: invoices already issued, timed by when customers usually pay rather than the due date, plus recurring revenue and a realistic guess at new sales.",
          "List cash out week by week: wages and super, rent, loan repayments, supplier bills already sitting in your accounting file, and any known one off costs.",
          "Add tax as its own line: BAS, PAYG withholding if it's paid separately, income tax instalments. These are the payments that blindside owners most often.",
          "Work out the closing balance each week and carry it forward as next week's opening balance.",
        ],
      },
      {
        type: "p",
        text: "If your books are current in Xero or similar, most of this already exists as unpaid invoices and bills sitting in the system. Building the forecast is mostly a matter of resorting information you already have, by date instead of by account.",
      },
      { type: "h2", text: "A worked example" },
      {
        type: "p",
        text: "Here's a simplified four week extract for an example services business starting with $42,000 in the bank. Every figure below is illustrative only.",
      },
      {
        type: "table",
        headers: ["", "Week 1", "Week 2", "Week 3", "Week 4"],
        rows: [
          ["Opening balance", "$42,000", "$34,500", "$47,500", "$21,500"],
          ["Cash in", "$18,000", "$36,000", "$9,000", "$28,000"],
          ["Wages and super", "$19,500", "$0", "$19,500", "$0"],
          ["Suppliers and rent", "$6,000", "$23,000", "$4,500", "$7,000"],
          ["BAS payment", "$0", "$0", "$11,000", "$0"],
          ["Closing balance", "$34,500", "$47,500", "$21,500", "$42,500"],
        ],
      },
      {
        type: "p",
        text: "Look at week 3. The balance more than halves because a pay run and the BAS land in the same week while collections are light. There's nothing wrong with this business, but an owner who can see it coming has options: pull an invoice follow up forward, push a supplier payment back a few days. An owner who only watches the balance fall has neither.",
      },
      {
        type: "callout",
        title: "Forecast the payment date, not the due date",
        text: "The biggest forecasting mistake is timing customer receipts by invoice due dates rather than how customers actually behave. If people pay you 12 days late on average, build that into the forecast. An overly optimistic forecast is worse than no forecast at all: it tells you everything's fine right up until it isn't.",
      },
      { type: "h2", text: "Keeping it alive in ten minutes a week" },
      {
        type: "p",
        text: "Pick a fixed time, Monday morning works for most people, and run through three things: update the opening balance to what actually happened, check last week's forecast against reality, and roll the whole thing forward a week. That middle step is where the learning happens. If receipts keep landing under forecast, that's telling you something about your debtors, not just your spreadsheet.",
      },
      {
        type: "p",
        text: "Resist the urge to add more detail. A forecast with eight cash out categories that actually gets updated every week beats a forty line masterpiece abandoned by month two. The goal isn't precision, it's an early warning.",
      },
      { type: "h2", text: "Where NextEra fits in" },
      {
        type: "p",
        text: "We reconcile client files weekly, which means the inputs for a reliable forecast are already sitting in the books we manage, and our management reporting includes a rolling cash flow view alongside the usual profit reporting. Want a clear read on where your cash position is headed and what's actually driving it? The Strategic Finance Review is a sensible place to start.",
      },
    ],
    faq: [
      {
        question: "What is a 13 week cash flow forecast?",
        answer:
          "A rolling week by week estimate of cash in and cash out over the next quarter. You drop the completed week, add a new one at the end, and update everything to actuals as you go.",
      },
      {
        question: "How is a cash flow forecast different from a budget?",
        answer:
          "A budget sets targets for income and expenses over a year, usually on an accruals basis. A cash flow forecast tracks the timing of actual money movements instead, including things a budget tends to ignore, like GST payments, loan principal and customer payment delays.",
      },
      {
        question: "What should I include in a cash flow forecast?",
        answer:
          "Opening bank balance, expected customer receipts timed by real payment behaviour, wages and super, supplier payments, rent, loan repayments, and tax such as BAS and income tax instalments, with the closing balance each week carrying into the next.",
      },
      {
        question: "How often should I update my cash flow forecast?",
        answer:
          "Weekly. Anything less often and the forecast drifts away from your actual bank balance and loses its early warning value.",
      },
    ],
    related: ["profit-vs-cash-explained", "debtor-management-rhythm", "working-capital-explained"],
  },
  {
    slug: "profit-vs-cash-explained",
    title: "Profitable but broke: profit vs cash explained",
    metaTitle: "Profit vs Cash Flow Explained | NextEra Bookkeeping",
    description:
      "Why profitable businesses still run out of cash, and how timing, debtors, stock and tax payments create the gap between profit and money in the bank.",
    category: "Cash flow",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "profit vs cash flow",
      "profitable but no cash",
      "why is my business profitable but broke",
      "difference between profit and cash",
    ],
    intro:
      "Profit and cash are not the same thing, and plenty of businesses learn that the hard way. A business can post a healthy profit and still not be able to cover Friday's payroll, because profit measures what you earned while cash measures what's actually landed in the bank. Unpaid invoices, stock on the shelf, loan repayments, equipment purchases and tax bills all move cash without moving profit in the same period. Once you understand that gap, a lot of confusing months start making sense.",
    sections: [
      { type: "h2", text: "Two different questions, two different answers" },
      {
        type: "p",
        text: "Your profit and loss answers one question: did the work you did this period create more value than it cost? It records income when you invoice and expenses when they're incurred. Your bank account answers a blunter question: can you pay people this week? Both matter. They just don't always agree.",
      },
      {
        type: "p",
        text: "Neither number beats the other. Profit tells you whether the business model works. Cash tells you whether you'll survive long enough to enjoy it. Plenty of businesses were profitable on paper the month they closed their doors. It runs the other way too: a business can look cash rich for months while quietly trading at a loss, coasting on customer deposits it'll eventually have to work off.",
      },
      { type: "h2", text: "The five usual suspects behind the gap" },
      {
        type: "ul",
        items: [
          "Debtors: you booked the income the moment you invoiced, but the customer hasn't paid yet. Growing sales usually means growing debtors, and that swallows cash right when things feel busiest.",
          "Stock: every dollar sitting on the shelf is a dollar of cash turned into product. It only becomes cash again when it sells, and only becomes an expense once it does.",
          "Loan principal: repaying borrowed money reduces your bank balance but never touches the profit and loss. Only the interest portion counts as an expense.",
          "Equipment and assets: a $40,000 vehicle might hit profit as depreciation spread over years, but the cash can leave your account in a single day.",
          "Tax and GST: GST you collect was never your money to begin with, and income tax on profit falls due regardless of whether customers have actually paid you. Both drain the bank without touching reported profit.",
        ],
      },
      { type: "h2", text: "A worked example" },
      {
        type: "p",
        text: "Take an example wholesale business having what looks like a strong quarter. The figures below are illustrative.",
      },
      {
        type: "table",
        headers: ["Item", "Profit view", "Cash view"],
        rows: [
          ["Sales invoiced", "$300,000", ""],
          ["Cash actually collected", "", "$240,000"],
          ["Cost of goods expensed", "$180,000", ""],
          ["Stock purchased and paid for", "", "$210,000"],
          ["Operating expenses (paid)", "$70,000", "$70,000"],
          ["Loan principal repaid", "not on P&L", "$15,000"],
          ["Result for the quarter", "$50,000 profit", "$55,000 cash outflow"],
        ],
      },
      {
        type: "p",
        text: "The owner sees a $50,000 profit and feels good about it, reasonably enough. Meanwhile the bank balance dropped $55,000 over the same quarter. Nothing dishonest is going on here: $60,000 of those sales are still sitting in debtors, the business stocked up ahead of next quarter, and the loan repayment left as it always does. But spend as though that profit is cash in hand and trouble arrives fast.",
      },
      {
        type: "callout",
        title: "Profit is an opinion about a period; cash is a fact",
        text: "Profit depends on accounting choices, depreciation methods, timing of recognition, judgement calls. Your bank balance depends on nothing at all. When the two disagree, trust the bank balance for survival decisions and the profit figure for strategy decisions.",
      },
      { type: "h2", text: "How to keep both numbers in view" },
      {
        type: "p",
        text: "The fix isn't picking a favourite number, it's looking at both side by side every month. A simple monthly pack should show profit, the movement in debtors, the movement in stock, loan repayments and upcoming tax, so you can see exactly where this month's profit actually went. Once you've watched that reconciliation play out a few times, the profitable but broke feeling stops being a mystery: you can point straight at the line responsible. Most accounting platforms can also produce a formal cash flow statement if you'd rather see the same reconciliation as a standard report.",
      },
      {
        type: "p",
        text: "It changes behaviour too. Owners who watch the debtor movement line start chasing overdue invoices sooner. Owners who watch stock levels stop over ordering. The reporting is the easy part; the habits it builds are the actual payoff.",
      },
      { type: "h2", text: "Seeing your own gap clearly" },
      {
        type: "p",
        text: "NextEra's monthly management reports show profit and cash movement side by side, with the drivers of any difference spelled out in plain language rather than accounting jargon. If your profit figure and your bank balance have been telling you two different stories lately, a Strategic Finance Review can trace exactly where the cash is going.",
      },
    ],
    faq: [
      {
        question: "How can a business be profitable but have no cash?",
        answer:
          "Profit records income when it's invoiced and expenses when they're incurred, not when money actually moves. Cash can end up locked in unpaid invoices and stock, or drained by loan principal, equipment purchases and tax payments that never appear as expenses on the P&L.",
      },
      {
        question: "Which matters more, profit or cash flow?",
        answer:
          "Honestly, both, just for different decisions. Cash flow determines whether you survive the short term: can you meet payroll, can you pay the BAS on time. Profit determines whether the business model is worth running at all. A healthy business needs both numbers positive over time, not one propping up the other.",
      },
      {
        question: "Why do loan repayments not show up on my profit and loss?",
        answer:
          "Only the interest portion of a repayment counts as an expense. The principal repays a debt sitting on your balance sheet, so it reduces your bank balance without touching your profit, which is a common source of confusion when profit looks fine but cash keeps shrinking.",
      },
      {
        question: "Is the GST I collect part of my income?",
        answer:
          "No. GST you charge on sales belongs to the ATO and gets paid over through your BAS, so good bookkeeping keeps it out of your profit figures, and wise owners keep it out of their spending decisions too.",
      },
    ],
    related: [
      "cash-flow-forecasting-guide",
      "working-capital-explained",
      "debtor-management-rhythm",
    ],
  },
  {
    slug: "break-even-point-guide",
    title: "Knowing your break-even point (and actually using it)",
    metaTitle: "Break-Even Point: How to Calculate and Use It",
    description:
      "How to calculate your break-even point, what to include in the numbers, and practical ways Australian business owners can use it for pricing and decisions.",
    category: "Cash flow",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "break even point",
      "how to calculate break even",
      "break even analysis small business",
      "contribution margin",
    ],
    intro:
      "How many sales do you actually need before you stop working for free? That's the question your break-even point answers: the level of sales at which your business covers every cost and lands on exactly zero profit. Work it out by dividing your fixed costs by your contribution margin, the slice of each sale left after variable costs, and a vague money worry turns into a concrete daily or weekly target. It sharpens pricing decisions too, and spending ones.",
    sections: [
      { type: "h2", text: "The two ingredients: fixed costs and contribution margin" },
      {
        type: "p",
        text: "Fixed costs turn up whether you sell anything or not: rent, insurance, software subscriptions, base salaries, loan interest. Variable costs rise and fall with each sale: materials, direct labour on jobs, merchant fees, freight. Contribution margin is what's left from each sales dollar after variable costs, and it's the pool of money available to cover the fixed costs.",
      },
      {
        type: "p",
        text: "The formula itself is short. Break-even sales equal fixed costs divided by contribution margin ratio. Fixed costs of $30,000 a month, keeping 40 cents of every sales dollar after variable costs, means $75,000 in monthly sales to break even. Everything above that line contributes 40 cents in the dollar to profit; everything below it burns cash.",
      },
      { type: "h2", text: "A worked example" },
      {
        type: "p",
        text: "Take an example cafe, all figures invented for illustration. Monthly fixed costs, rent, core roster wages, insurance, utilities, add up to $46,000. Average customer spend is $18, and the variable cost per sale, mostly ingredients and packaging, is $6.30, leaving $11.70 contribution per sale, a 65 percent ratio.",
      },
      {
        type: "p",
        text: "Break-even sales come out at $46,000 divided by 0.65, roughly $70,800 a month. At an $18 average spend that's about 3,930 customers a month, or around 130 a day if the doors are open every day. Now the owner has a number to check against the till each afternoon, rather than a vague sense of whether the month's going well.",
      },
      { type: "h2", text: "What owners actually do with the number" },
      {
        type: "ul",
        items: [
          "Set a daily or weekly floor: knowing you need 130 customers a day turns a 95 customer day into a signal to act, not just a quiet one.",
          "Test price changes before making them: lift the average spend from $18 to $19 and the daily break-even customer count drops noticeably; the arithmetic takes two minutes.",
          "Weigh up new fixed costs honestly: a $1,500 monthly software subscription needs roughly $2,300 of extra monthly sales at a 65 percent margin. Worth it or not, at least the question is now something you can actually answer.",
          "Plan around seasonality: if January reliably trades below break-even, the surplus from November and December has a defined job to do.",
          "Sanity check growth plans: hiring someone lifts the fixed cost line, so you can work out the sales needed to justify the role before you advertise it.",
        ],
      },
      {
        type: "callout",
        title: "Include your own pay in fixed costs",
        text: "Leave a fair wage for yourself out of the fixed costs and the whole calculation quietly lies to you. If the business only breaks even while you work for free, it hasn't actually broken even. Put a realistic owner salary in there and let the number tell the truth.",
      },
      { type: "h2", text: "Keeping the calculation honest over time" },
      {
        type: "p",
        text: "Break-even isn't a set and forget figure. Rent rises, supplier prices move, and whatever you sell shifts your average contribution margin over time. Recalculate whenever a major cost changes, and do it at least a couple of times a year regardless. Watch how you classify costs too: some are stepped rather than truly fixed, adding a staff member once volume passes a threshold, say, and treating those as permanently fixed will flatter the number.",
      },
      {
        type: "p",
        text: "If your books are well kept, the inputs come straight off your profit and loss. A clean chart of accounts that separates direct costs from overheads turns the whole exercise into a ten minute job instead of an archaeology project.",
      },
      { type: "h2", text: "Getting to a number you can trust" },
      {
        type: "p",
        text: "NextEra structures client files so direct costs and overheads stay cleanly separated, which means your break-even point can be read almost straight off the monthly management report. Never seen your number, or suspect the one you worked out years ago has gone stale? Our Strategic Finance Review works it through with current figures.",
      },
    ],
    faq: [
      {
        question: "What is the break-even point formula?",
        answer:
          "Break-even sales equal fixed costs divided by the contribution margin ratio, which is sales minus variable costs expressed as a percentage of sales. $30,000 of fixed costs at a 40 percent margin, for example, means $75,000 of sales to break even.",
      },
      {
        question: "What counts as a fixed cost?",
        answer:
          "Anything that doesn't change with sales volume in the short term: rent, insurance, subscriptions, base wages and salaries, loan interest. Include a realistic owner salary so the figure reflects a business that actually pays you.",
      },
      {
        question: "How often should I recalculate my break-even point?",
        answer:
          "Whenever a significant cost changes, a rent increase, a new hire, a supplier price rise, and at least twice a year as a habit either way. Product mix changes can shift your average contribution margin without any single obvious trigger, so it pays to check even when nothing dramatic has happened.",
      },
      {
        question: "Is break-even analysis useful for service businesses?",
        answer:
          "Yes. Variable costs are often small for a service business, so the contribution margin runs high and break-even ends up driven mostly by fixed costs, wages especially, which effectively converts your overhead base into a required amount of billable work per week or month.",
      },
    ],
    related: ["budgets-owners-actually-use", "job-and-project-margins", "profit-vs-cash-explained"],
  },
  {
    slug: "job-and-project-margins",
    title: "Job and project margins: finding where you make money",
    metaTitle: "Job and Project Margins: Find Where You Make Money",
    description:
      "How to track job and project margins so you can see which work actually makes money, with a simple method any Australian business can run in Xero.",
    category: "Reporting",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "job costing",
      "project profitability",
      "gross margin by job",
      "job margin tracking",
      "tracking categories xero",
    ],
    intro:
      "A tradie we could be describing right now is having a great year on paper: the phone doesn't stop, the crew's flat out, revenue's up on last year. Then the accountant asks which jobs actually made money, and it turns out nobody's ever separated one job's numbers from another's. Job and project margin tracking fixes that: record the income and direct costs of each job on its own, and you see the gross profit each one actually delivered instead of one blended figure for the whole business. Most businesses that start doing this discover the same uncomfortable thing, a handful of jobs generate most of the profit, and a few are quietly losing money. Finding out which is which changes how you quote, who you hire, and sometimes which customers you keep.",
    sections: [
      { type: "h2", text: "Why the blended margin hides the truth" },
      {
        type: "p",
        text: "A profit and loss shows one gross margin for everything you did in the period. See 35 percent and it feels like every job made 35 percent. In reality it might be a mix of jobs at 55 percent and jobs at 8 percent, and the average hides both. The profitable work ends up subsidising the unprofitable work, and because nobody can see it happening, the unprofitable work keeps getting quoted the exact same way.",
      },
      {
        type: "p",
        text: "The pattern turns up everywhere: trades, agencies, consultancies, manufacturers, even medical practices comparing service lines. Anywhere revenue arrives through distinct jobs, projects or engagements, a per job view is available, and it's usually revealing.",
      },
      { type: "h2", text: "What to capture against each job" },
      {
        type: "ul",
        items: [
          "Income: every invoice, progress claim and variation billed for the job.",
          "Materials and direct purchases: supplier bills coded to the job as they're entered, not reconstructed later from memory.",
          "Direct labour: hours worked on the job at a loaded cost rate that includes super and leave loadings, not just the base hourly wage.",
          "Subcontractors and outsourced work tied to the job.",
          "Job specific costs: equipment hire, freight, travel, permit fees.",
        ],
      },
      {
        type: "p",
        text: "Overheads like rent and office wages stay out of the job margin entirely. The question at this level is simple: did the direct income exceed the direct cost of doing the work, and by how much? Whether the whole pool of job margins covers your overheads is the break-even question, and it's worth keeping that separate.",
      },
      { type: "h2", text: "A worked example of what the numbers reveal" },
      {
        type: "p",
        text: "Here are four illustrative jobs from an example building and maintenance business, each of which looked fine at quoting time.",
      },
      {
        type: "table",
        headers: ["Job", "Invoiced", "Direct costs", "Gross margin", "Margin %"],
        rows: [
          ["Office fit out", "$84,000", "$61,000", "$23,000", "27%"],
          ["School maintenance contract", "$36,000", "$19,500", "$16,500", "46%"],
          ["Custom residential reno", "$127,000", "$118,000", "$9,000", "7%"],
          ["Insurance repair panel work", "$22,000", "$13,000", "$9,000", "41%"],
        ],
      },
      {
        type: "p",
        text: "The renovation was the biggest job by a mile and felt like the flagship project of the year. It produced less gross profit than a $22,000 insurance job, and ate up months of the team's best labour doing it. Without job level tracking, the owner remembers the reno as a good year's work. With it, next year's quoting on custom renovations changes, or the business stops chasing them altogether.",
      },
      {
        type: "callout",
        title: "Labour is where job margins go to die",
        text: "Materials get coded to jobs easily enough, there's a bill sitting right there. Labour is where it leaks: hours get recorded casually or not at all, and base wage rates understate what an hour truly costs once super, leave and downtime are factored in. Fix one thing first, and make it how labour hours and cost rates hit your jobs.",
      },
      { type: "h2", text: "Setting it up without drowning in admin" },
      {
        type: "p",
        text: "In Xero, tracking categories give a light job level view that suits businesses running a modest number of concurrent jobs. Dedicated job costing add-ons suit builders and firms juggling many projects with progress claims. The tool matters less than the discipline behind it: every sales invoice and supplier bill gets a job code on entry, timesheets get filled in honestly, and someone actually reviews a margin by job report each month while the jobs are still fresh enough to learn from.",
      },
      {
        type: "p",
        text: "Start with your five biggest current jobs rather than trying to code the whole business at once. One month of real data on real jobs teaches more than a quarter spent designing the perfect structure.",
      },
      { type: "h2", text: "Turning job data into better decisions" },
      {
        type: "p",
        text: "NextEra sets up job and project tracking as part of client bookkeeping and builds margin by job reporting into the monthly management pack, so the learning happens every month instead of at year end. Suspect some of your work is quietly subsidising the rest? A Strategic Finance Review is a sensible way to test the hunch against actual numbers.",
      },
    ],
    faq: [
      {
        question: "What is a good gross margin for a job or project?",
        answer:
          "It varies too widely by industry and job type for a universal number to mean much. The more useful comparison is internal: rank your own jobs by margin percentage and look at the spread between your best and worst.",
      },
      {
        question: "What is the difference between gross margin and net profit on a job?",
        answer:
          "Gross margin is job income minus the direct costs of doing that job, materials, direct labour, subcontractors. Net profit also deducts a share of overheads like rent and admin wages. Job level reporting usually stops at gross margin and leaves overheads to the whole of business view.",
      },
      {
        question: "Do I need special software to track job profitability?",
        answer:
          "Not necessarily. Xero tracking categories handle a light version fine for businesses with a manageable number of jobs, and dedicated job costing tools only really earn their keep once you're running many concurrent projects, progress claims or detailed labour costing.",
      },
      {
        question: "How should I cost labour against jobs?",
        answer:
          "Use a loaded cost rate that includes super, leave entitlements and other on costs, not the bare hourly wage, and factor in non billable time if you want the rate to reflect what an hour genuinely costs the business. Your bookkeeper or accountant can help set a defensible figure.",
      },
    ],
    related: ["break-even-point-guide", "xero-tracking-categories", "budgets-owners-actually-use"],
  },
  {
    slug: "budgets-owners-actually-use",
    title: "Budgets owners actually use",
    metaTitle: "Business Budgets Owners Actually Use | NextEra",
    description:
      "How to build a short, useful business budget you will actually check each month, and how to review it against actuals without pain or spreadsheet guilt.",
    category: "Reporting",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "small business budget",
      "budget vs actuals",
      "how to create a business budget",
      "annual budget small business",
    ],
    intro:
      "Most budgets fail for a boring reason: not bad maths, just built once, in too much detail, then never looked at again. A useful business budget is a short, realistic estimate of next year's income and expenses that you check actuals against every single month. Get the level of detail right and it survives contact with a busy year. Get it wrong and it joins the pile of spreadsheets nobody opens after March.",
    sections: [
      { type: "h2", text: "What a budget is actually for" },
      {
        type: "p",
        text: "A budget isn't a prediction and it isn't a cage. Think of it as a reference point. Its job is to make one monthly question answerable: is the business tracking the way we intended, and if not, where exactly is the gap? Without a budget, a monthly profit and loss is just a number floating with no context. With one, every line compares against an intention, and the gaps turn into actual conversations.",
      },
      {
        type: "p",
        text: "That framing takes the pressure off getting it perfectly right. Your budget will be wrong, every budget is. Its value comes from being wrong in a specific, visible way that teaches you something. Twenty percent over on subcontractor costs is information you can act on. A general feeling that costs are up isn't.",
      },
      { type: "h2", text: "Build it in an afternoon, not a month" },
      {
        type: "ul",
        items: [
          "Start from last year's actual profit and loss, exported month by month. History is the most honest starting point you've got.",
          "Adjust income for what you genuinely know: contracts won or lost, price rises already scheduled, capacity you've added. Skip the round number growth target with no plan sitting behind it.",
          "Adjust the big expense lines one by one: wages for planned hires and pay rises, rent for known increases, any large one offs you can see coming.",
          "Sweep the small lines together: minor expense categories can be grouped and simply indexed up rather than agonised over line by line.",
          "Sanity check the bottom line: does the resulting profit fund your drawings, your tax and any debt repayments? If not, the budget's telling you something important before the year's even started.",
        ],
      },
      {
        type: "p",
        text: "Keep it monthly, not annual divided by twelve. If December's quiet and October's huge, the budget should say so, otherwise every month's comparison starts with an excuse built in.",
      },
      { type: "h2", text: "A worked example of a monthly review" },
      {
        type: "p",
        text: "Here's an illustrative budget versus actual extract for one month in an example consulting business.",
      },
      {
        type: "table",
        headers: ["Line", "Budget", "Actual", "Variance"],
        rows: [
          ["Consulting income", "$95,000", "$88,400", "-$6,600"],
          ["Wages and super", "$52,000", "$51,700", "+$300"],
          ["Contractors", "$8,000", "$14,200", "-$6,200"],
          ["Software and subscriptions", "$2,400", "$3,100", "-$700"],
          ["All other overheads", "$11,500", "$11,900", "-$400"],
          ["Net profit", "$21,100", "$7,500", "-$13,600"],
        ],
      },
      {
        type: "p",
        text: "The month looks bad on the surface, but the review takes ten minutes because the causes are right there: income came in light and contractor spend nearly doubled. The follow up questions write themselves. Was the contractor spend covering a staff absence, and is that finished now? Is the income dip just timing, or the start of a trend? One month of variance is a prompt. Three months of the same variance is a decision.",
      },
      {
        type: "callout",
        title: "Review the variances, not the budget",
        text: "The monthly ritual isn't re-reading the budget, it's looking at the five biggest variances and asking why. Ignore the small noise. A rule like only chasing variances over ten percent and over $1,000 keeps the review short enough to actually survive a busy month.",
      },
      { type: "h2", text: "When to re-forecast and when to hold" },
      {
        type: "p",
        text: "Leave the original budget intact for the year so you can see how reality diverged from what you intended. If something changes dramatically, a major contract won or lost, add a re-forecast alongside it rather than overwriting history. Comparing actuals to both tells you how well you adapt, as well as how well you plan.",
      },
      {
        type: "p",
        text: "Loading the budget into your accounting software rather than parking it in a separate spreadsheet makes the monthly comparison automatic. Xero and similar platforms will spit out budget versus actual reports on demand once the numbers are in.",
      },
      { type: "h2", text: "Making the monthly review effortless" },
      {
        type: "p",
        text: "NextEra loads client budgets into Xero and builds the budget versus actual comparison into the monthly management report, with the significant variances already flagged and explained in plain language. Budget living in a forgotten spreadsheet, or never had one that actually earned its keep? The Strategic Finance Review is a good way to reset the whole rhythm.",
      },
    ],
    faq: [
      {
        question: "How detailed should a small business budget be?",
        answer:
          "Detailed where it matters, brief everywhere else. Income streams and the handful of largest expense categories, wages especially, deserve individual attention; small overhead lines can be grouped and indexed from last year.",
      },
      {
        question: "Should a budget be monthly or annual?",
        answer:
          "Build it monthly so it reflects your real seasonality, then let the annual total fall out of the months. An annual figure divided by twelve makes every seasonal month look like a variance, which trains you to ignore the report.",
      },
      {
        question: "What is a budget variance and when should I act on one?",
        answer:
          "A variance is the gap between what you budgeted and what actually happened for a line in a period. Investigate the largest few each month using a threshold, ten percent and a minimum dollar amount works for most businesses. A variance that repeats for several months is a signal to change something, not just a footnote.",
      },
      {
        question: "Should I update my budget during the year?",
        answer:
          "Keep the original for reference and add a re-forecast if something major shifts, winning or losing a significant contract, say. Overwriting the budget mid year erases the record of what you originally expected, and that's where most of the learning actually lives.",
      },
    ],
    related: ["cash-flow-forecasting-guide", "break-even-point-guide", "monthly-close-checklist"],
  },
  {
    slug: "working-capital-explained",
    title: "Working capital: the number that quietly runs your business",
    metaTitle: "Working Capital Explained for Business Owners",
    description:
      "What working capital is, how to work out your cash conversion cycle, and practical ways to free up cash tied in debtors, stock and supplier payments.",
    category: "Cash flow",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "working capital",
      "cash conversion cycle",
      "working capital management",
      "how to improve working capital",
    ],
    intro:
      "Working capital is the quiet tax on growing a business: the money tied up just keeping the wheels turning, what customers owe you plus the stock you're holding, minus what you owe your suppliers. It's why growing businesses so often feel poorer as sales climb; every extra sale locks up more cash in debtors and stock before it ever comes back. Manage it well and you can release cash you've already earned without selling a single extra thing.",
    sections: [
      { type: "h2", text: "The working capital equation in plain terms" },
      {
        type: "p",
        text: "Take the invoices your customers haven't paid yet, add the stock and work in progress you're holding, then subtract the supplier bills you haven't paid yet. What's left is the cash your operations absorb just to exist. A figure of $150,000 means exactly that much of your money is permanently out on loan to your own business, funding the gap between paying for things and getting paid for them. Accountants sometimes define working capital more broadly off the balance sheet, but for day to day management this operating version is the one worth watching.",
      },
      {
        type: "p",
        text: "This is why growth strains cash so badly. Double your sales with the same payment terms and stock habits and you roughly double the cash trapped in the cycle. The profit's real, it just arrives later; the working capital demand turns up first. Plenty of businesses that fail during a growth spurt die of exactly this mechanism.",
      },
      { type: "h2", text: "The cash conversion cycle: your money's lap time" },
      {
        type: "p",
        text: "The cash conversion cycle measures how many days a dollar spends trapped in the loop: the days your stock sits before selling, plus the days customers take to pay you, minus the days you take to pay suppliers. Here's an illustrative example for a products business.",
      },
      {
        type: "table",
        headers: ["Component", "Example days", "Direction"],
        rows: [
          ["Stock held before sale", "45 days", "cash out"],
          ["Customers take to pay", "38 days", "cash out"],
          ["You take to pay suppliers", "30 days", "cash back"],
          ["Cash conversion cycle", "53 days", "total"],
        ],
      },
      {
        type: "p",
        text: "In this example every dollar spent on stock takes 53 days to come home. Turn over $2 million a year and shortening that cycle by even a week releases a meaningful amount of cash permanently, not as a one off, because the loop keeps running continuously. That's cash without new sales, new debt or new investors.",
      },
      { type: "h2", text: "Practical levers, in rough order of effort" },
      {
        type: "ul",
        items: [
          "Invoice immediately: every day between finishing work and sending the invoice adds a day to your cycle for nothing. Same day invoicing is the cheapest working capital improvement there is.",
          "Tighten payment terms and follow up consistently: shorter terms on new customers plus a reliable reminder rhythm shave days off collections without any confrontation.",
          "Take deposits or progress payments on larger jobs so customers fund the work as it happens, rather than after.",
          "Right size stock: identify the slow movers, buy them less deeply, and stop reordering things whose only job is filling a shelf.",
          "Use supplier terms fully but respectfully: paying on the agreed date rather than early is free funding; paying late damages relationships and does something else entirely.",
        ],
      },
      {
        type: "callout",
        title: "Watch the trend, not the snapshot",
        text: "A single working capital figure means little on its own. Track debtor days, stock days and creditor days monthly and watch the direction they're moving. Debtor days creeping from 35 to 44 over a quarter is an early warning that arrives months before the bank balance ever feels it.",
      },
      { type: "h2", text: "When working capital needs funding anyway" },
      {
        type: "p",
        text: "Some businesses run structurally long cycles: importers paying overseas suppliers upfront, manufacturers with long production runs, contractors carrying retentions. For these, even excellent management leaves a gap, and that gap is exactly what overdrafts, trade finance and invoice finance exist to bridge. Whether and how to fund it is a decision for you and your advisers, but make it from a clear measurement of the cycle rather than a recurring overdraft surprise. Interest on that funding is a genuine cost of the cycle too, which is one more reason shortening it pays twice.",
      },
      { type: "h2", text: "Measuring it properly every month" },
      {
        type: "p",
        text: "Debtor days, stock days and the cash conversion cycle are standard inclusions in NextEra's monthly management reporting, so any movement shows up while it's still cheap to fix. Never had your own cycle measured? The Strategic Finance Review calculates it from your actual books and shows exactly where the cash is hiding.",
      },
    ],
    faq: [
      {
        question: "What is working capital in simple terms?",
        answer:
          "The cash tied up in running the business: unpaid customer invoices plus stock on hand, minus unpaid supplier bills. It's money you've earned or spent that hasn't cycled back to your bank account yet.",
      },
      {
        question: "What is the cash conversion cycle?",
        answer:
          "The number of days between paying for stock or work and actually receiving payment from the customer. Calculated as stock days plus debtor days minus creditor days. A shorter cycle means cash comes back faster and the business needs less funding to operate.",
      },
      {
        question: "Why does my business feel short of cash when sales are growing?",
        answer:
          "Growth increases the cash locked in debtors and stock before the matching payments arrive, and the faster you grow, the more cash the cycle absorbs. That's normal and manageable, but only if you can actually see the cycle and plan the funding for it.",
      },
      {
        question: "How can I reduce my working capital needs?",
        answer:
          "Invoice the same day work's done, follow up debtors on a consistent rhythm, take deposits on large jobs, trim slow moving stock, and use your full agreed supplier terms. Small improvements across each lever compound into a noticeably shorter cycle.",
      },
    ],
    related: [
      "profit-vs-cash-explained",
      "debtor-management-rhythm",
      "cash-flow-forecasting-guide",
    ],
  },
  {
    slug: "debtor-management-rhythm",
    title: "A debtor management rhythm that collects cash",
    metaTitle: "Debtor Management: A Rhythm That Collects Cash",
    description:
      "A simple weekly debtor management rhythm for Australian businesses: clear terms, prompt invoicing, scheduled follow ups and far fewer overdue accounts.",
    category: "Cash flow",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "debtor management",
      "accounts receivable process",
      "how to get invoices paid faster",
      "overdue invoices small business",
    ],
    intro:
      "The businesses that get paid fastest are rarely the pushiest ones chasing every invoice with a phone call the moment it's a day late. They're the most consistent: clear terms agreed upfront, invoices sent the moment the work's done, follow ups that arrive on a schedule customers can set their watch by. Good debtor management is a fixed weekly rhythm, not a burst of frantic calls when the bank balance gets tight. Here's a rhythm you can run in under an hour a week.",
    sections: [
      { type: "h2", text: "Collection starts before the invoice exists" },
      {
        type: "p",
        text: "Most late payment problems get created at the start of the relationship, not at the end. Terms that were never actually discussed, invoices missing a purchase order number, work delivered to a customer who was never going to pay on time, all of it guarantees slow cash later. The pre work matters: agree payment terms in writing before starting, confirm exactly what the customer's accounts process needs on an invoice, and for larger jobs consider a deposit, which quietly tests payment behaviour before you're heavily committed. For new commercial customers of any size, a quick check of trading references before extending credit terms is cheap insurance.",
      },
      {
        type: "p",
        text: "Then invoice immediately. An invoice sent the day the work finishes starts the clock at the earliest possible moment and lands while the value's still fresh in the customer's mind. Send it three weeks later and you've signalled you're in no hurry, so the customer matches your energy.",
      },
      { type: "h2", text: "The weekly rhythm" },
      {
        type: "p",
        text: "Set a fixed time each week, Tuesday at 9am, say, and work the aged receivables report from oldest to newest. The report tells you exactly who owes what and for how long; the rhythm decides what happens at each stage. A workable sequence looks like this.",
      },
      {
        type: "ul",
        items: [
          "A few days before due date: a short friendly reminder that the invoice is coming up, which catches lost invoices and approval bottlenecks before they turn into lateness.",
          "One day overdue: an automatic reminder email. Polite, factual, invoice attached again, payment details front and centre.",
          "Seven days overdue: a phone call, not another email. Ask if there's any problem with the invoice; most of the time it's a mundane blockage a two minute call clears up.",
          "Fourteen days overdue: a firmer written follow up confirming the call, restating the amount, proposing a specific payment date.",
          "Beyond that: a decision point. A payment plan, pausing further work, director contact, or a debt collection process, and which one's right depends on the customer and the amount involved.",
        ],
      },
      {
        type: "p",
        text: "The magic isn't in any single step. It's that every debtor learns your follow ups always arrive, on schedule, every single time. Customers triage their payables, and suppliers who reliably follow up get paid ahead of the ones who don't.",
      },
      {
        type: "callout",
        title: "The seven day phone call does the heavy lifting",
        text: "Email reminders are easy to ignore. A friendly phone call isn't. Frame it as checking whether anything's wrong with the invoice rather than demanding money, and it preserves the relationship, surfaces genuine disputes early, and reliably beats another templated email.",
      },
      { type: "h2", text: "Use the automation, keep the judgement" },
      {
        type: "p",
        text: "Modern accounting software will send invoice reminders automatically on whatever schedule you set, and online payment options on the invoice itself remove friction at the exact moment a customer's willing to pay. Automate the routine steps without a second thought. Keep the phone calls and escalation decisions human, because tone and context matter once an account's genuinely overdue. Monthly statements to account customers add another gentle layer too, catching invoices that individual reminders missed.",
      },
      {
        type: "p",
        text: "Watch two numbers monthly: debtor days, roughly how long customers take to pay on average, and the percentage of your receivables sitting beyond 30 days overdue. Improving those two numbers is the whole point of the rhythm, and they respond within a couple of months of running it consistently.",
      },
      { type: "h2", text: "Making the rhythm someone's actual job" },
      {
        type: "p",
        text: "A rhythm only works if someone owns it, and in a lot of small businesses it silently belongs to nobody at all. NextEra runs debtor follow up rhythms for clients as part of ongoing bookkeeping, with aged receivables and debtor days tracked in the monthly management report. Receivables quietly crept up on you? The Strategic Finance Review shows exactly what that pattern looks like, and what it's costing.",
      },
    ],
    faq: [
      {
        question: "How do I get customers to pay invoices faster?",
        answer:
          "Agree terms in writing before the work starts, invoice the same day it's done, make paying easy with online payment options, and follow up on a fixed schedule that never lapses. Consistency beats aggression here: customers pay reliable follow ups first.",
      },
      {
        question: "When should I call a customer about an overdue invoice?",
        answer:
          "A phone call around a week overdue works far better than another email. Frame it as checking whether there's a problem with the invoice. Most delays turn out to be mundane, a lost invoice, an approval bottleneck, and a call resolves them quickly.",
      },
      {
        question: "What are debtor days?",
        answer:
          "A measure of how long, on average, your customers take to pay you, and tracked monthly the trend tells you whether collections are improving or slipping, usually well before the bank balance makes it obvious.",
      },
      {
        question: "Should I charge late fees or interest on overdue invoices?",
        answer:
          "You can, if your terms provide for it, and simply having a late fee clause tends to encourage punctuality on its own. Whether enforcing it is worthwhile depends on the relationship; consistent follow up usually matters more than penalties in practice.",
      },
    ],
    related: [
      "working-capital-explained",
      "cash-flow-forecasting-guide",
      "tax-invoice-requirements-australia",
    ],
  },
  {
    slug: "bookkeeping-for-tradies",
    title: "Bookkeeping for tradies: progress claims, retentions and cash",
    metaTitle: "Bookkeeping for Tradies: Claims, Retentions, Cash",
    description:
      "Bookkeeping for tradies and builders: progress claims, retentions, deposits and job cash flow, and how to keep your books matching your contracts.",
    category: "Industries",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "bookkeeping for tradies",
      "progress claims bookkeeping",
      "retentions accounting",
      "builder bookkeeping australia",
    ],
    intro:
      "A tradie we could be describing right now is flat out on a $180,000 fit out, three variations deep, with $12,000 sitting in retentions from a job finished last winter that nobody's ever chased. Bookkeeping for trades and construction businesses carries three complications most other industries never see: progress claims that bill work in stages, retentions where customers hold back part of your money for months or years, and job costs that need tracking against specific projects. Handle those three well and the rest is ordinary bookkeeping. Handle them badly and your books will tell you comforting lies about jobs that are actually losing money.",
    sections: [
      { type: "h2", text: "Progress claims are not ordinary invoices" },
      {
        type: "p",
        text: "On larger jobs you bill in stages, a deposit, then claims at defined milestones or monthly based on work completed. Every claim needs to reconcile back to the contract value, including approved variations, so at any moment you can answer two questions: how much of this contract have we claimed, and how much's left? Books that treat each claim as an unrelated invoice lose that running position, and disputes about what's been billed turn into arguments nobody can settle from the records.",
      },
      {
        type: "p",
        text: "Variations deserve their own discipline. Work done on a verbal say so, priced later and billed from memory, is exactly where margin quietly disappears. Every variation should be documented, priced and approved before it's built, then billed as a visible line so the paper trail matches what actually happened on site.",
      },
      { type: "h2", text: "Retentions: earned money you cannot spend yet" },
      {
        type: "p",
        text: "Many commercial contracts let the customer withhold a percentage of each claim, commonly released in parts at practical completion and after the defects liability period. That money is yours, it just might not arrive for a year or more. The bookkeeping needs to show retention receivables separately from normal debtors, with expected release dates, so the money's neither forgotten nor mistakenly counted as available cash.",
      },
      {
        type: "ul",
        items: [
          "Record the retention withheld on each claim at the time of the claim, not whenever someone remembers later.",
          "Keep a retention schedule by job showing amounts held, release conditions and expected dates.",
          "Review it whenever a job reaches practical completion or a defects period ends, because unclaimed retentions are simply donations at that point.",
          "Treat retention releases as a distinct line in your cash flow forecast rather than lumping them in with general collections.",
        ],
      },
      {
        type: "callout",
        title: "Follow up your retention releases",
        text: "Retention money doesn't release itself. In practice it's often only paid when the contractor asks for it. A simple schedule with release dates, reviewed monthly, is one of the highest value habits in construction bookkeeping, and businesses that skip it routinely leave earned money sitting with the customer indefinitely.",
      },
      { type: "h2", text: "Job costing keeps the quoting honest" },
      {
        type: "p",
        text: "Every supplier bill, subcontractor invoice and labour hour should land against a job code the moment it's entered. The payoff is a live answer to the question that matters mid project: are we still on track against the quote? As an illustration, a job quoted at $180,000 with costs budgeted at $135,000 might show $110,000 of costs booked at sixty percent completion. That trajectory points toward roughly $183,000 of final cost, and knowing it with forty percent of the job still to go gives you options a post job autopsy never can.",
      },
      {
        type: "p",
        text: "Subcontractor payments carry an extra obligation: businesses in building and construction generally need to report payments to contractors to the ATO through the taxable payments annual report. Clean, correctly coded subcontractor records kept all year turn that report into a formality instead of a scramble; check the current requirements on the ATO website or with your adviser.",
      },
      { type: "h2", text: "Cash flow around GST and the claim cycle" },
      {
        type: "p",
        text: "The gap between paying for materials and labour and actually receiving a progress claim payment is the defining cash flow feature of the industry, and retentions stretch it further still. GST adds its own timing questions, since whether you account for GST on a cash or accruals basis changes when GST on claims falls due; the right setting depends on your circumstances, so confirm it with your adviser or the ATO. A weekly cash flow view that includes claim dates, retention releases and BAS payments stops these known events arriving as surprises.",
      },
      { type: "h2", text: "Books that match the job site" },
      {
        type: "p",
        text: "NextEra works with trade and construction clients on exactly these mechanics: progress claim tracking, retention schedules, job costing and management reports that show margin by job while the job's still running. Books unable to tell you which jobs made money and where your retentions sit? A Strategic Finance Review is a practical way to get that visibility.",
      },
    ],
    faq: [
      {
        question: "How should progress claims be recorded in my books?",
        answer:
          "Each claim should be recorded against the specific job and reconcile to a running contract position: contract value plus approved variations, less what's already been claimed. That keeps a live figure for how much of each contract still needs billing.",
      },
      {
        question: "What are retentions and how do I account for them?",
        answer:
          "Retentions are amounts a customer withholds from progress claims, typically released after practical completion and the defects period. Record them as a separate receivable by job with expected release dates, and follow them up actively, because in practice they're often only paid when requested.",
      },
      {
        question: "Do tradies need to report subcontractor payments to the ATO?",
        answer:
          "Businesses in building and construction that pay contractors generally need to lodge a taxable payments annual report with the ATO. The detailed rules depend on your situation, so check current ATO guidance or ask your adviser.",
      },
      {
        question: "Should a trade business account for GST on a cash or accruals basis?",
        answer:
          "It depends on eligibility and circumstances, and it changes when GST on progress claims becomes payable, which can matter a lot when claims are large and payment's slow, so make this a decision with your accountant rather than a default setting.",
      },
    ],
    related: ["job-and-project-margins", "debtor-management-rhythm", "fuel-tax-credits-basics"],
  },
  {
    slug: "bookkeeping-for-medical-dental",
    title: "Bookkeeping for medical and dental practices",
    metaTitle: "Bookkeeping for Medical and Dental Practices",
    description:
      "Bookkeeping issues specific to medical and dental practices: patient receipting, health fund and Medicare payments, practitioner splits and reporting.",
    category: "Industries",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "medical practice bookkeeping",
      "dental practice bookkeeping",
      "bookkeeping for doctors australia",
      "practice management reconciliation",
    ],
    intro:
      "A patient walks out after a consultation having paid a small gap fee by card. Behind that one transaction sits a health fund payment landing in three days, and a Medicare rebate arriving weeks later in a batch bundled with dozens of other patients. Medical and dental bookkeeping is dominated by exactly this problem: income arrives in fragments, recorded first in practice management software and then again in the accounting file, and the two systems rarely agree by accident. Add practitioner payment arrangements and service entity structures on top, and a practice needs deliberately designed bookkeeping rather than a generic small business setup.",
    sections: [
      { type: "h2", text: "Why practice income is hard to reconcile" },
      {
        type: "p",
        text: "A single consultation can generate several money movements: a patient gap paid at the desk by card, a health fund portion arriving days later, or a bulk billed amount paid by Medicare in a batch that bundles many patients together. Your practice management system records the clinical billing; your bank feed shows lump sum settlements that combine and net all of these flows. Reconciling one to the other is the core discipline of practice bookkeeping.",
      },
      {
        type: "p",
        text: "The standard approach uses clearing accounts: billing from the practice software posts into a clearing account, and the batched deposits from Medicare, health funds and card terminals clear against it. When the clearing account returns to zero, everything billed has been banked. When it doesn't, something's missing, an unbanked amount, a rejected claim, a keying error, and the balance points straight at it.",
      },
      { type: "h2", text: "Practitioner arrangements shape the whole file" },
      {
        type: "p",
        text: "Many practices engage practitioners who aren't employees, operating instead under facility or service arrangements, with the practice collecting fees and remitting a share, or the practitioner billing patients directly and paying a service fee. The bookkeeping has to mirror the actual legal arrangement precisely, because money collected on behalf of a practitioner isn't practice income and should never be reported as if it were. Get this wrong and revenue, GST treatment and BAS reporting all distort at once.",
      },
      {
        type: "p",
        text: "These arrangements also carry payroll tax and contractor classification considerations that have drawn significant attention from state revenue offices in recent years. The bookkeeping can't settle those questions on its own, but clean records of how money actually flows are exactly what your accountant and advisers need when they do. Never had the arrangement documentation and the money flows compared side by side? Worth a conversation with your adviser.",
      },
      { type: "h2", text: "GST needs more care than most practices expect" },
      {
        type: "p",
        text: "Many health services are GST-free, but a practice's income is rarely all health services. Cosmetic procedures, reports for insurers or lawyers, room rental to other practitioners and retail product sales can each carry different GST treatment. The chart of accounts should separate these income types so GST gets applied per stream rather than under one blanket assumption. The classification rules are situational, so confirm the treatment of specific services with the ATO or your adviser.",
      },
      { type: "h2", text: "What a well run practice file looks like" },
      {
        type: "ul",
        items: [
          "Daily or weekly reconciliation of practice software billing to banking through clearing accounts, so discrepancies surface within days.",
          "Income split by practitioner and by type, consultations, procedures, product sales, matching the real arrangements.",
          "Practitioner remittances calculated and paid on a documented, auditable basis.",
          "GST coding by income stream rather than one default setting.",
          "A monthly management report showing income per practitioner, per chair or room utilisation where relevant, and overheads against income.",
        ],
      },
      {
        type: "callout",
        title: "Your practice software is not your accounting system",
        text: "Practice management platforms are excellent at clinical billing and appointment management, but they're not a general ledger. Treat the accounting file as the source of financial truth, reconciled to the practice software regularly. Practices that report from billing software alone routinely misstate income and GST.",
      },
      { type: "h2", text: "As an illustration of the payoff" },
      {
        type: "p",
        text: "Consider an example two dentist practice where reconciliations had drifted for a quarter. Tidying the clearing accounts turned up an illustrative $9,400 of health fund claims that had been rejected and never resubmitted, sitting invisible in the gap between the two systems. Nothing clever was needed to find it, just the reconciliation discipline that makes gaps like that impossible to miss.",
      },
      { type: "h2", text: "Specialist support for practice finances" },
      {
        type: "p",
        text: "NextEra manages bookkeeping for medical and dental practices with clearing account reconciliation, practitioner level reporting and GST coding by income stream built in from the start. Practice software and accounting file quietly stopped agreeing with each other? Our Strategic Finance Review will establish the true position, and a rhythm to maintain it.",
      },
    ],
    faq: [
      {
        question: "Why does my practice management software not match my accounting file?",
        answer:
          "Because Medicare, health funds and card terminals settle in batches that combine many patient transactions, while the practice software records each billing individually. Clearing accounts that reconcile billing to banking are the standard fix, and the residual balance highlights anything missing.",
      },
      {
        question: "Is medical income GST-free?",
        answer:
          "Many health services are, but practices often have other income streams too, reports for third parties, cosmetic work, room rental, product sales, that may be treated differently. Code each income stream separately and confirm the specific treatment with the ATO or your adviser.",
      },
      {
        question: "How should payments to practitioners be recorded?",
        answer:
          "In a way that mirrors the actual legal arrangement. If the practice collects fees on a practitioner's behalf, those collections aren't practice income and the remittance isn't simply an expense, which matters for revenue reporting, GST and payroll tax analysis alike.",
      },
      {
        question: "How often should a medical or dental practice reconcile?",
        answer:
          "Daily or weekly is best, since rejected health fund and Medicare claims have resubmission time limits in practice, so the sooner a gap's visible, the more likely the money gets recovered.",
      },
    ],
    related: [
      "bookkeeping-for-professional-services",
      "common-gst-mistakes",
      "outsourced-vs-in-house-finance",
    ],
  },
  {
    slug: "bookkeeping-for-professional-services",
    title: "Bookkeeping for professional services firms",
    metaTitle: "Bookkeeping for Professional Services Firms",
    description:
      "Bookkeeping for professional services firms: WIP, utilisation, recoverable expenses and the reports that show whether client work actually pays.",
    category: "Industries",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "professional services bookkeeping",
      "work in progress accounting",
      "utilisation and recovery rates",
      "law firm accounting basics",
    ],
    intro:
      "Ask a partner how the firm's doing and you'll usually get a vibe, not a number: flat out, everyone's busy, should be a good year. In a professional services firm the product is time, so the bookkeeping questions look different from most industries: how much unbilled work are we sitting on, how much of our people's time actually turns into invoices, and what did each engagement really earn per hour of effort? Firms that track work in progress, utilisation and recovery alongside the ordinary books can see profitability per client and per job. Firms that don't are guessing, and usually guessing optimistically.",
    sections: [
      { type: "h2", text: "Work in progress: the invisible asset" },
      {
        type: "p",
        text: "Work in progress, WIP, is time your team's spent on client work that hasn't been billed yet. It's real value the firm has created, but it shows up nowhere in a standard profit and loss until the invoice actually goes out. A firm can feel flat out for two months while revenue looks terrible, simply because the work's piling up as WIP instead of converting to invoices. Tracking WIP by client and matter makes that pipeline visible.",
      },
      {
        type: "p",
        text: "WIP ages too, and old WIP behaves a lot like old debtors: the longer time sits unbilled, the harder it gets to bill in full. Clients query invoices for work done months ago, and partners quietly write numbers down before the invoice's even drafted. A monthly WIP review, billing promptly and confronting stale balances head on, protects revenue that's already been earned in every sense except the paperwork.",
      },
      { type: "h2", text: "Utilisation and recovery: the two honesty metrics" },
      {
        type: "p",
        text: "Utilisation measures how much of available time gets spent on client work. Recovery, sometimes called realisation, measures how much of the time recorded actually gets billed and collected at full value. Together the two explain most of a firm's financial performance. As an illustration only: a consultant employed for 40 hours a week who records 26 client hours has 65 percent utilisation; if discounting and write offs mean only 22 of those hours get effectively invoiced, recovery of the recorded time sits around 85 percent, and the combined effect is that barely half the employment week converts to revenue.",
      },
      {
        type: "p",
        text: "Neither metric needs fancy systems, but both need honest timesheets. Time recorded vaguely, reconstructed at the end of the week from memory, produces flattering nonsense. Time recorded daily against real matters produces actual management information.",
      },
      { type: "h2", text: "The rest of the professional services file" },
      {
        type: "ul",
        items: [
          "Recoverable expenses and disbursements: costs incurred on a client's behalf need tracking to the matter and on billing, with GST treatment handled correctly; unbilled disbursements are pure margin leakage.",
          "Fixed fee engagements: track time against them anyway, because a fixed fee with untracked effort is a margin mystery, and quoting the next one accurately depends on knowing what the last one really took.",
          "Leave liabilities: in a business whose costs are overwhelmingly salaries, accrued leave is a significant and growing liability that belongs on the monthly report, not something discovered annually.",
          "Trust accounts: firms holding client money, legal practices especially, face strict and audited trust accounting obligations under state rules; trust money must never mix with office money, and the specific requirements come from the relevant regulator.",
          "Revenue timing: when revenue gets recognised on longer engagements affects both reporting and tax, and it's worth a conversation with your accountant.",
        ],
      },
      {
        type: "callout",
        title: "Bill promptly; WIP is not wine",
        text: "Unbilled time doesn't improve with age, whatever it might feel like. Firms that invoice monthly against WIP, while the work's fresh and the value obvious, bill more of what they record and collect it faster. One habit, a fixed monthly billing cycle protected from busy periods, outperforms almost any other improvement available.",
      },
      { type: "h2", text: "What the monthly report should show" },
      {
        type: "p",
        text: "Alongside the standard profit and loss, a professional services management report earns its keep with a small set of specifics: WIP balance and ageing, utilisation by fee earner, recovery of recorded time, debtor days, and profitability by client or matter for the significant engagements. Together they answer the question the profit and loss alone can't: which work and which clients are actually worth the hours.",
      },
      { type: "h2", text: "Reporting built for time based businesses" },
      {
        type: "p",
        text: "NextEra builds these firm specific measures, WIP, utilisation, recovery, client profitability, into the monthly management reporting for professional services clients. Reports currently stopping at revenue and expenses? A Strategic Finance Review can establish which clients and engagements genuinely pay for the time they consume.",
      },
    ],
    faq: [
      {
        question: "What is WIP in a professional services firm?",
        answer:
          "Work in progress is client work performed but not yet invoiced, usually measured as recorded time at charge rates plus unbilled disbursements. It's earned value that doesn't appear in revenue until billed, and it should be reviewed and billed monthly because it gets harder to recover as it ages.",
      },
      {
        question: "What do utilisation and recovery mean?",
        answer:
          "Utilisation is the share of available working time spent on client work, while recovery is the share of recorded time actually billed and collected rather than discounted or written off. Together they show how effectively the firm converts payroll into revenue.",
      },
      {
        question: "Should we track time on fixed fee work?",
        answer:
          "Yes. The fee may be fixed but the cost of delivering it isn't, and without time records you can't know the margin on the engagement or price the next one sensibly.",
      },
      {
        question: "What are trust accounting obligations?",
        answer:
          "Firms that hold money on behalf of clients, legal practices being the obvious example, must keep it in a compliant trust account, strictly separated from the firm's own funds. Record keeping and audit requirements come from the relevant state regulator, and the rules are specific and non negotiable. Take guidance from the regulator and your adviser rather than guessing.",
      },
    ],
    related: ["job-and-project-margins", "xero-tracking-categories", "budgets-owners-actually-use"],
  },
  {
    slug: "bookkeeping-for-ecommerce",
    title: "E-commerce bookkeeping: platforms, payouts and GST",
    metaTitle: "E-commerce Bookkeeping: Payouts and GST | NextEra",
    description:
      "E-commerce bookkeeping explained: platform payout clearing accounts, marketplace fees, GST on sales and imports, refunds and inventory basics.",
    category: "Industries",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "ecommerce bookkeeping",
      "shopify bookkeeping australia",
      "amazon seller accounting",
      "gst on online sales",
      "payout clearing account",
    ],
    intro:
      "A payout notification lands on Tuesday morning for $8,540. It feels like Tuesday's sales figure, and it isn't even close. The defining problem of e-commerce bookkeeping is that the money landing in your bank account is never your sales figure: platforms deposit payouts that bundle many orders together and arrive net of fees, refunds and adjustments. Record those deposits as revenue and you understate sales, hide costs, and get GST wrong, all in one move. The fix is a settlement based approach using clearing accounts, plus deliberate handling of GST, refunds and inventory.",
    sections: [
      { type: "h2", text: "Why the bank deposit is not your sales number" },
      {
        type: "p",
        text: "Take that $8,540 payout as an illustration. Behind it might sit $9,600 of orders, less $610 of platform and payment fees, less $370 of refunds, less $80 of chargebacks and adjustments. Book the deposit straight as $8,540 of sales and your revenue's understated, your fees are invisible, and the GST on the true sales figure is wrong. Multiply that across every payout on every channel and the accounts drift a long way from reality.",
      },
      {
        type: "p",
        text: "Gross sales, fees, refunds and adjustments each need recording in their own right, with the net payout simply clearing the balance. That's what a clearing account structure does.",
      },
      { type: "h2", text: "Clearing accounts: one per channel" },
      {
        type: "p",
        text: "Each sales channel gets its own clearing account. Sales settlement data posts the gross figures into the clearing account, and the bank deposit clears it out. If the clearing account doesn't return to near zero each cycle, something's been missed: an unrecorded fee, a refund, a timing difference, a missing payout.",
      },
      {
        type: "ul",
        items: [
          "Post gross sales, fees, refunds and adjustments from the platform's settlement or payout reports, not from the bank feed.",
          "Match each bank deposit against the relevant clearing account rather than coding it straight to sales.",
          "Reconcile each channel's clearing account monthly at minimum, weekly for high volume stores.",
          "Use connector tools where the volume justifies them, but check their postings, because a misconfigured integration automates errors at scale.",
          "Keep channels separate in reporting so you can see margin by channel after each platform's fees.",
        ],
      },
      { type: "h2", text: "GST across platforms, imports and exports" },
      {
        type: "p",
        text: "GST in e-commerce has more moving parts than a shopfront ever does. Domestic sales by a GST registered business generally carry 10 percent GST, but the details multiply fast: some marketplaces collect and remit GST on certain sales themselves, imported stock can involve GST at the border, low value imported goods run their own regime, and genuine exports may be GST-free. Which of these applies to your store depends on your channels, your customers and your supply chain, so map your actual flows against current ATO guidance or sit down with an adviser rather than assuming one rule covers everything.",
      },
      {
        type: "p",
        text: "The practical bookkeeping consequence: GST coding needs deciding per channel and per transaction type, and platform settlement reports, which usually itemise the tax treatment applied, are the source documents worth holding onto.",
      },
      {
        type: "callout",
        title: "Marketplace collected GST still needs correct books",
        text: "Where a marketplace collects and remits GST on a sale, recording it as if you collected the GST yourself overstates your BAS liability; ignoring the sale altogether understates income. Your settlement reports show who remitted what. Reflect that split in the books and your BAS follows it correctly.",
      },
      { type: "h2", text: "Inventory, refunds and the other recurring characters" },
      {
        type: "p",
        text: "Stock is usually an e-commerce store's largest asset, and cost of goods sold its largest cost, so how inventory gets tracked determines whether your margin figures mean anything at all. Periodic stocktakes with adjustment journals suit smaller operations; perpetual inventory systems suit scale. Either way, landed cost matters: freight, duties and import charges belong in the cost of your stock, not lost in general expenses, or every margin report flatters you.",
      },
      {
        type: "p",
        text: "Refunds and chargebacks deserve their own accounts rather than being netted silently against sales, because their trend is diagnostic: rising refund rates flag product or fulfilment problems, rising chargebacks flag payment risk. Multi currency sales add another layer, with exchange differences to record as foreign currency payouts convert. None of this is difficult individually; the trap is volume, which is why structure and automation matter more here than in most industries.",
      },
      { type: "h2", text: "Channel level clarity every month" },
      {
        type: "p",
        text: "NextEra sets up e-commerce clients with per channel clearing accounts, settlement based revenue recording and management reports that show sales, fees and margin by channel. Bank deposits currently standing in for your sales figures? A Strategic Finance Review will quantify the gap and set up a structure that reports the business as it really trades.",
      },
    ],
    faq: [
      {
        question: "Should I record platform payouts as sales?",
        answer:
          "No. Payouts arrive net of fees, refunds and adjustments, so recording them as sales understates revenue and hides costs. Record gross sales, fees and refunds from the platform's settlement reports instead, and let the payout clear a dedicated clearing account.",
      },
      {
        question: "What is a payout clearing account?",
        answer:
          "A holding account in your books for each sales channel, where gross sales, fees and refunds post in from settlement reports and bank deposits clear it back out. A balance that doesn't return to near zero each cycle signals a missed transaction or timing difference.",
      },
      {
        question: "Do I charge GST on online sales?",
        answer:
          "Domestic sales by a GST registered Australian business generally include 10 percent GST, but marketplace collected GST, imports, low value imported goods and exports each carry their own treatment. Map your specific channels against current ATO guidance or ask an adviser.",
      },
      {
        question: "How should an online store track inventory?",
        answer:
          "Smaller stores can run periodic stocktakes with adjusting journals; higher volume stores generally need perpetual inventory tracking instead. Either way, include freight, duties and import costs in the landed cost of stock so margin reporting reflects what products truly cost.",
      },
    ],
    related: ["common-gst-mistakes", "gst-registration-guide-australia", "xero-bank-feeds-guide"],
  },
];
